import {shallowMemoize} from '@sail/utils';
import createGlobalizeWithLocale from 'src/internal/createGlobalizeWithLocale/createGlobalizeWithLocale';
import getCldrDisplayNameValue from 'src/internal/display-name-formatter/getCldrDisplayNameValue';
import mergeOptions from 'src/internal/mergeOptions';

import type {IReports} from '@sail/observability';
import type {
  FormatDisplayNameOptions,
  FormatDisplayNameOptionsDefaults,
  FormatDisplayNameOptionsWithDefaults,
} from 'src/public/formatDisplayName/types';
import type {IntlConfig, Locale} from 'src/types';

const displayNameFormatter = shallowMemoize(
  (
    config: IntlConfig,
    locale: Locale,
    type: FormatDisplayNameOptions['type'],
    reports: IReports,
  ) => {
    const globalize = createGlobalizeWithLocale(
      config,
      locale,
      undefined,
      reports,
    );

    return (value: string): string => {
      if (!value) {
        return '';
      }

      return getCldrDisplayNameValue(globalize, type, value, reports);
    };
  },
);

/**
 * @example Currencies {{include "./examples/formatDisplayName.currency.tsx"}}
 * @example Languages {{include "./examples/formatDisplayName.language.tsx"}}
 * @example Regions {{include "./examples/formatDisplayName.region.tsx"}}
 *
 * @see https://sail.stripe.me/apis/intl/useIntl/#format-display-name
 */
export default function formatDisplayName(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  /**
   * - If the type is `"region"`, `code` should be:
   *   - For countries: a [two-letter ISO 3166 code](https://www.iso.org/iso-3166-country-codes.html).
   *   - For continents and subcontinents: a [three-digit UN M49 code](https://unstats.un.org/unsd/methodology/m49/).
   * - If the type is `"language"`, `code` should be:
   *   - A [BCP 47 language tag](https://www.rfc-editor.org/rfc/rfc5646.html#section-2).
   *   - A two-letter [ISO 639-1 language code](https://www.iso.org/standard/22109.html).
   *   - A three-letter [ISO 639-2 language code](https://www.iso.org/standard/4767.html) in case a two letter code doesn't exist.
   * - If the type is `"currency"`, `code` should be:
   *   - A three-letter [ISO 4217 currency code](https://www.iso.org/iso-4217-currency-codes.html).
   *   - A non-ISO currency code like `USDC` for USD Coin or `BTC` for Bitcoin.
   */
  code: string,
  /** An object with options to customize the formatting. */
  options: FormatDisplayNameOptions,
): string {
  const defaults: FormatDisplayNameOptionsDefaults = {
    locale: config.locale,
  };

  const mergedOptions: FormatDisplayNameOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
  );

  const formatter = displayNameFormatter(
    config,
    mergedOptions.locale,
    mergedOptions.type,
    reports,
  );

  return formatter(code);
}

export type FormatDisplayName = typeof formatDisplayName;

export type FormatDisplayNameDeclarative = (
  date: Parameters<FormatDisplayName>[2],
  options: Parameters<FormatDisplayName>[3],
) => ReturnType<FormatDisplayName>;
